import { AuthDialog } from "@/components/auth.dialog";
import { BidBattleCard } from "@/components/battle";
import { useUser } from "@/hooks/user";
import { m } from "@/paraglide/messages.js";
import { Button, ButtonLink } from "@bidbattles/bloks/components/ui/button";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	EmblaHorizontalScrollPlugin,
} from "@bidbattles/bloks/components/ui/carousel";
import { Link } from "@bidbattles/bloks/components/ui/link";
import {
	BBBackendQueryOptions,
	type BidBattle,
} from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { ArrowRight } from "@untitledui/icons";
import { PlayCircle } from "untitledui-js/react";
import z from "zod";

const searchParamsSchema = z.object({
	auth: z
		.enum(["login", "signup", "forgot_password", "reset_password"])
		.optional(),
	referral_code: z.string().optional(),
	token: z.coerce.string().optional(),
});

export const Route = createFileRoute("/")({
	async loader({ context }) {
		await context.queryClient.ensureQueryData(
			BBBackendQueryOptions.listBattles({ limit: 8, completed: false }),
		);
	},
	validateSearch: zodValidator(searchParamsSchema),
	component: App,
});

// <h1 className="text-gray-900 font-bold text-4xl leading-11 md:text-7xl md:leading-[5.625rem]">
// 	{m.home__hero__title()}
// </h1>
function App() {
	const user = useUser();
	const { auth, referral_code, token } = Route.useSearch();
	const query = useSuspenseQuery(
		BBBackendQueryOptions.listBattles({ limit: 8, completed: false }),
	);
	const battles = query.data.battles;

	return (
		<main className="flex flex-col gap-y-8 items-center grow bg-gray-warm-100">
			<section className="bg-white flex flex-col items-center w-full py-12">
				<div className="p-box flex flex-col gap-y-8 max-w-container w-full">
					<div className="flex flex-col gap-y-3">
						<p className="text-gray-500 font-semibold text-sm leading-5">
							{m.home__hero__prefix()}
						</p>
						<h1 className="text-gray-900 font-bold">
							<span className="text-2xl leading-8 md:text-3xl md:leading-10">
								{m.home__hero__title_part_1()}
							</span>
							<br />
							<span className="text-5xl leading-11 md:text-7xl md:leading-22.5">
								{m.home__hero__title_part_2()}
							</span>
						</h1>
						<p className="text-gray-500 text-lg leading-7">
							{m.home__hero__subtitle_1()}
							<br className="hidden md:block" />
							<span className="md:hidden">&nbsp;</span>
							{m.home__hero__subtitle_2()}
						</p>
					</div>

					<div className="w-full flex flex-col md:flex-row items-stretch gap-4">
						<AuthDialog
							defaultMode="signup"
							hidden={!!user}
							trigger={<Button>{m.home__hero__cta()}</Button>}
						/>
						<ButtonLink
							variant="outline"
							to="/how-it-works"
							className="flex items-center gap-2 justify-center"
						>
							<PlayCircle size="1em" />
							{m.home__hero__secondary_cta()}
						</ButtonLink>
					</div>
				</div>
			</section>
			<BattleSection name="Battles" battles={battles} />
			<AuthDialog
				defaultMode={auth}
				defaultOpen={!!auth && !user}
				referralCode={referral_code}
				resetPasswordToken={token}
			/>
		</main>
	);
}

function BattleSection({
	name,
	battles,
}: { name: string; battles: BidBattle[] }) {
	if (battles.length === 0) {
		return null;
	}

	return (
		<section className="w-full p-box max-w-container mx-auto flex flex-col gap-6 md:gap-8">
			<div className="w-full flex items-center justify-between">
				<h2 className="font-semibold text-[1.25rem] leading-[1.875rem] md:text-2xl md:leading-8">
					{name}
				</h2>
			</div>
			<div className="flex flex-wrap gap-8 mb-6 justify-center">
				{battles.map((battle) => (
					<BidBattleCard
						key={battle.id}
						className={cn("basis-1/1 sm:basis-1/3 md:basis-1/4 md:max-w-70")}
						battle={battle}
						render={(props) => (
							<Link
								to="/battle/$id/details"
								params={{ id: battle.id }}
								{...props}
							/>
						)}
					/>
				))}
			</div>
			<div className="flex justify-center items-center gap-4 w-full">
				<ButtonLink variant="white" to="/battles">
					View all
					<ArrowRight size="1em" />
				</ButtonLink>
			</div>
		</section>
	);
}
